import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Building2, Cpu, Server, ChevronDown, ChevronUp } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { log } from "../../utils/logger";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AGE_BADGE = {
  Moderno: { color: "#00FF66", bg: "#00FF66" },
  Aggiornabile: { color: "#FF9F0A", bg: "#FF9F0A" },
  Obsoleta: { color: "#FF3B30", bg: "#FF3B30" },
};

function techScore(introYear) {
  const age = new Date().getFullYear() - introYear;
  if (age < 2) return Math.floor(Math.random() * 20) + 81;
  if (age < 5) return Math.floor(Math.random() * 30) + 51;
  if (age < 10) return Math.floor(Math.random() * 30) + 21;
  return Math.floor(Math.random() * 21);
}

function ageBadge(introYear) {
  const age = new Date().getFullYear() - introYear;
  if (age < 2) return "Moderno";
  if (age < 5) return "Aggiornabile";
  return "Obsoleta";
}

function ScoreBar({ score }) {
  const color = score >= 75 ? "#00FF66" : score >= 50 ? "#FF9F0A" : "#FF3B30";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[#1F242D] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-bold w-8 text-right" style={{ color, fontFamily: "JetBrains Mono, monospace" }}>{score}</span>
    </div>
  );
}

function CompanyTechCard({ company }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  const avgScore = company.technologies.length
    ? Math.round(company.technologies.reduce((s, t) => s + t.score, 0) / company.technologies.length)
    : 0;
  const overallColor = avgScore >= 75 ? "#00FF66" : avgScore >= 50 ? "#FF9F0A" : "#FF3B30";

  const techByCategory = useMemo(() => ({
    software: company.technologies.filter(t => t.category === "software"),
    hardware: company.technologies.filter(t => t.category === "hardware"),
  }), [company.technologies]);

  return (
    <div className="bg-[#0D0F12] border border-white/[0.04]" data-testid="tech-company-card">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-4 px-5 py-4 border-b border-white/[0.04] hover:bg-[#1A1D24] transition-colors">
        <Building2 className="w-4 h-4 text-[#00E5FF] flex-shrink-0" />
        <span className="text-sm text-white font-medium flex-1 text-left">{company.name}</span>
        <div className="flex items-center gap-3 mr-4">
          <span className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">Score Tech:</span>
          <span className="text-sm font-bold" style={{ color: overallColor, fontFamily: "JetBrains Mono, monospace" }}>{avgScore}</span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#A0AEC0]" /> : <ChevronDown className="w-4 h-4 text-[#A0AEC0]" />}
      </button>

      {open && (
        <div className="p-5 space-y-4">
          {/* Software */}
          {techByCategory.software.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-3.5 h-3.5 text-[#00E5FF]" />
                <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">{t("tech.software")}</p>
              </div>
              <div className="space-y-2">
                {techByCategory.software.map((tech) => {
                  const badge = ageBadge(tech.intro_year);
                  const badgeCfg = AGE_BADGE[badge];
                  return (
                    <div key={tech.id || tech.name} className="flex items-center gap-3 p-3 bg-[#050505] border border-white/[0.04]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs text-white font-medium">{tech.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 border" style={{ color: badgeCfg.color, borderColor: `${badgeCfg.color}40`, backgroundColor: `${badgeCfg.color}10` }}>{badge}</span>
                          <span className="text-[10px] text-[#A0AEC0]">{t("tech.since")} {tech.intro_year}</span>
                        </div>
                        <ScoreBar score={tech.score} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Hardware */}
          {techByCategory.hardware.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Server className="w-3.5 h-3.5 text-[#FF9F0A]" />
                <p className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">{t("tech.hardware")}</p>
              </div>
              <div className="space-y-2">
                {techByCategory.hardware.map((tech) => {
                  const badge = ageBadge(tech.intro_year);
                  const badgeCfg = AGE_BADGE[badge];
                  return (
                    <div key={tech.id || tech.name} className="flex items-center gap-3 p-3 bg-[#050505] border border-white/[0.04]">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs text-white font-medium">{tech.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 border" style={{ color: badgeCfg.color, borderColor: `${badgeCfg.color}40`, backgroundColor: `${badgeCfg.color}10` }}>{badge}</span>
                          <span className="text-[10px] text-[#A0AEC0]">{t("tech.since")} {tech.intro_year}</span>
                        </div>
                        <ScoreBar score={tech.score} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TechnologiesPage() {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("score_desc");

  useEffect(() => {
    axios.get(`${API}/tech/technologies`, { withCredentials: true })
      .then(r => setCompanies(r.data))
      .catch((err) => log.warn("[TechnologiesPage] load failed:", err))
      .finally(() => setLoading(false));
  }, []);

  const withScore = companies.map(c => ({
    ...c,
    avgScore: c.technologies.length
      ? Math.round(c.technologies.reduce((s, t) => s + t.score, 0) / c.technologies.length)
      : 0
  }));

  const sorted = [...withScore].sort((a, b) =>
    sortBy === "score_desc" ? b.avgScore - a.avgScore : a.avgScore - b.avgScore
  );

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{t("tech.title")}</h1>
          <p className="text-xs text-[#A0AEC0] mt-1 tracking-widest uppercase">{t("tech.subtitle")}</p>
        </div>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="bg-[#050505] border border-white/[0.04] text-white px-3 py-2 text-xs focus:outline-none focus:border-[#00E5FF]"
        >
          <option value="score_desc">Score Decrescente</option>
          <option value="score_asc">Score Crescente</option>
        </select>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap px-4 py-3 bg-[#0D0F12] border border-white/[0.04]">
        <span className="text-[10px] text-[#A0AEC0] tracking-widest uppercase">Legenda:</span>
        {[["Moderno", "#00FF66", "< 2 anni (81–100)"], ["Aggiornabile", "#FF9F0A", "2–10 anni (21–80)"], ["Obsoleta", "#FF3B30", "> 10 anni (0–20)"]].map(([l, c, d]) => (
          <div key={l} className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
            <span className="text-xs text-[#CBD5E1]">{l}</span>
            <span className="text-[10px] text-[#A0AEC0]">{d}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A0AEC0]">Caricamento tecnologie...</div>
      ) : (
        <div className="space-y-4">
          {sorted.map(c => <CompanyTechCard key={c.company_id} company={c} />)}
        </div>
      )}
    </div>
  );
}
